import { useQuery } from "@tanstack/react-query";
import { getFishingReportFn } from "@/lib/marine.functions";
import { COFFMAN_COVE } from "@/lib/fishing-data";

function formatTimestamp(iso: string | null | undefined) {
  if (!iso) return "unknown";
  return new Date(iso).toLocaleString("en-US", {
    timeZone: COFFMAN_COVE.timezone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * Stored narrative for the current report. Reads only from `fishing_reports`
 * / `conditions_snapshots` — never an external API. When the pipeline marked
 * the report stale or failed, no narrative is shown at all.
 */
export function ReportNarrativeCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["fishing-report-narrative"],
    queryFn: () => getFishingReportFn(),
    staleTime: 10 * 60_000,
  });

  const report = data?.report ?? null;
  const lastUpdated = data?.lastUpdated ?? report?.created_at ?? null;
  const unavailable =
    !isLoading && (!report || report.status !== "generated" || !report.narrative);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
      <p className="uppercase tracking-[0.3em] text-xs text-[var(--color-ember)]">
        Today's read
      </p>
      {isLoading ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading report…</p>
      ) : unavailable ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Conditions unavailable — last updated {formatTimestamp(lastUpdated)}
        </p>
      ) : (
        <>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-card-foreground">
            {report!.narrative!.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Last updated {formatTimestamp(lastUpdated)}
          </p>
        </>
      )}
    </div>
  );
}
